import { OfferNotificationTemplateChannel } from '@today/types'
import { FieldShell } from '@today/ui-components'
import { Button, Checkbox, Input, Textarea } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { MessagePreviewPane } from './message-preview-pane.view'
import { MessageVariableList } from './message-variable-list.view'
import {
	DEFAULT_TEMPLATE_CHANNEL_LABELS,
	type MessagePickerOption,
	type MessagePreviewView,
	type NotificationTemplateDraft,
} from './notification-template-types'

/** The form's built-in copy; override any entry for localisation. */
export interface NotificationTemplateFormLabels {
	name: string
	description: string
	channel: string
	locale: string
	subject: string
	body: string
	isDefault: string
	isActive: string
	variables: string
	previewOffer: string
	previewOfferPlaceholder: string
	preview: string
	previewTitle: string
	previewEmpty: string
	previewing: string
	save: string
	saving: string
	cancel: string
}

const DEFAULT_LABELS: NotificationTemplateFormLabels = {
	name: 'Name',
	description: 'Description',
	channel: 'Channel',
	locale: 'Locale',
	subject: 'Subject',
	body: 'Body',
	isDefault: 'Default for channel',
	isActive: 'Active',
	variables: 'Available variables',
	previewOffer: 'Preview offer',
	previewOfferPlaceholder: 'Choose an offer…',
	preview: 'Preview',
	previewTitle: 'Preview',
	previewEmpty: 'Pick an offer and press Preview to see the rendered message.',
	previewing: 'Rendering…',
	save: 'Save',
	saving: 'Saving…',
	cancel: 'Cancel',
}

/**
 * Props for {@link NotificationTemplateForm}.
 *
 * @property draft - The values being edited (controlled).
 * @property variables - The variable names a template can read, in display order.
 * @property offerOptions - The tenant's offers a preview can render against.
 * @property previewOfferId - The offer chosen for the preview, or `''`.
 * @property preview - The last rendered preview, or null.
 * @property previewError - A refused preview's message, or null.
 * @property error - A refused save's message, shown next to the form, or null.
 */
export interface NotificationTemplateFormProps {
	draft: NotificationTemplateDraft
	variables: readonly string[]
	maxBodyLength: number
	offerOptions: readonly MessagePickerOption[]
	previewOfferId: string
	preview: MessagePreviewView | null
	previewError: string | null
	isPreviewing: boolean
	isSaving: boolean
	error: string | null
	onChange: (draft: NotificationTemplateDraft) => void
	onPreviewOfferChange: (offerId: string) => void
	onPreview: () => void
	onSave: () => void
	onCancel: () => void
	channelLabels?: Partial<Record<OfferNotificationTemplateChannel, string>>
	labels?: Partial<NotificationTemplateFormLabels>
}

type Parts = NotificationTemplateFormProps & { labels: NotificationTemplateFormLabels }

const SELECT =
	'h-10 w-full rounded-ds-md border border-ds-border bg-ds-surface px-3 text-ds-base text-ds-text disabled:cursor-not-allowed disabled:opacity-50'

/** Reports `draft` with one field replaced. */
const changer =
	(props: Parts) =>
	<K extends keyof NotificationTemplateDraft>(key: K, value: NotificationTemplateDraft[K]) =>
		props.onChange({ ...props.draft, [key]: value })

/** Name, description, channel type and locale. */
function DetailFields(props: Parts): ReactElement {
	const { draft, labels } = props
	const set = changer(props)
	const channels = { ...DEFAULT_TEMPLATE_CHANNEL_LABELS, ...props.channelLabels }
	return (
		<div className="grid gap-4 md:grid-cols-2">
			<FieldShell label={labels.name}>
				<Input value={draft.name} maxLength={100} onChange={(e) => set('name', e.target.value)} />
			</FieldShell>
			<FieldShell label={labels.locale}>
				<Input value={draft.locale} maxLength={3} onChange={(e) => set('locale', e.target.value)} />
			</FieldShell>
			<FieldShell label={labels.channel}>
				<select
					className={SELECT}
					value={draft.channelType}
					onChange={(e) => set('channelType', e.target.value as OfferNotificationTemplateChannel)}
				>
					{Object.values(OfferNotificationTemplateChannel).map((channel) => (
						<option key={channel} value={channel}>
							{channels[channel]}
						</option>
					))}
				</select>
			</FieldShell>
			<FieldShell label={labels.description}>
				<Input value={draft.description} maxLength={500} onChange={(e) => set('description', e.target.value)} />
			</FieldShell>
		</div>
	)
}

/** The subject (EMAIL only, REQ-002) and the plain body textarea with its counter. */
function MessageFields(props: Parts): ReactElement {
	const { draft, labels } = props
	const set = changer(props)
	return (
		<div className="flex flex-col gap-4">
			{draft.channelType === OfferNotificationTemplateChannel.EMAIL ? (
				<FieldShell label={labels.subject}>
					<Input value={draft.subject} maxLength={200} onChange={(e) => set('subject', e.target.value)} />
				</FieldShell>
			) : null}
			<FieldShell label={labels.body} description={`${draft.body.length} / ${props.maxBodyLength}`}>
				<Textarea
					value={draft.body}
					rows={6}
					maxLength={props.maxBodyLength}
					onChange={(e) => set('body', e.target.value)}
				/>
			</FieldShell>
			<div className="flex flex-wrap gap-6">
				<Checkbox isSelected={draft.isDefaultForChannel} onChange={(on) => set('isDefaultForChannel', on)}>
					<span className="text-ds-sm text-ds-text">{labels.isDefault}</span>
				</Checkbox>
				<Checkbox isSelected={draft.isActive} onChange={(on) => set('isActive', on)}>
					<span className="text-ds-sm text-ds-text">{labels.isActive}</span>
				</Checkbox>
			</div>
		</div>
	)
}

/** The offer picker, the Preview action and the rendered result. */
function PreviewSection(props: Parts): ReactElement {
	const { labels } = props
	return (
		<div className="flex flex-col gap-3">
			<div className="flex items-end gap-2">
				<FieldShell label={labels.previewOffer} className="flex-1">
					<select
						className={SELECT}
						value={props.previewOfferId}
						onChange={(e) => props.onPreviewOfferChange(e.target.value)}
					>
						<option value="">{labels.previewOfferPlaceholder}</option>
						{props.offerOptions.map((offer) => (
							<option key={offer.value} value={offer.value}>
								{offer.label}
							</option>
						))}
					</select>
				</FieldShell>
				<Button
					variant="secondary"
					isDisabled={props.previewOfferId === '' || props.draft.body.trim() === '' || props.isPreviewing}
					onPress={props.onPreview}
				>
					{props.isPreviewing ? labels.previewing : labels.preview}
				</Button>
			</div>
			<MessagePreviewPane
				title={labels.previewTitle}
				preview={props.preview}
				error={props.previewError}
				emptyText={labels.previewEmpty}
				subjectLabel={labels.subject}
			/>
		</div>
	)
}

/** The refused-save message, Save and Cancel. */
function FormActions(props: Parts): ReactElement {
	const { labels } = props
	return (
		<div className="flex flex-col gap-2">
			{props.error ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{props.error}
				</p>
			) : null}
			<div className="flex justify-end gap-2">
				<Button variant="secondary" onPress={props.onCancel}>
					{labels.cancel}
				</Button>
				<Button isDisabled={props.isSaving} onPress={props.onSave}>
					{props.isSaving ? labels.saving : labels.save}
				</Button>
			</div>
		</div>
	)
}

/**
 * The create/edit form of a notification template (TO-140 offer-notification-templates
 * REQ-011): a subject only for EMAIL, a plain textarea body, the variable list, and
 * a Preview of the unsaved text against an offer the admin picks. Pure and
 * controlled: the draft, the preview and the errors are props; it neither saves
 * nor renders anything itself.
 */
export function NotificationTemplateForm(props: NotificationTemplateFormProps): ReactElement {
	const parts = { ...props, labels: { ...DEFAULT_LABELS, ...props.labels } }
	return (
		<form className="flex flex-col gap-6" onSubmit={(event) => event.preventDefault()}>
			<DetailFields {...parts} />
			<div className="grid gap-6 md:grid-cols-2">
				<MessageFields {...parts} />
				<MessageVariableList variables={props.variables} title={parts.labels.variables} />
			</div>
			<PreviewSection {...parts} />
			<FormActions {...parts} />
		</form>
	)
}
